/* VIEW — WINNERS. Exact port of code.txt WinnersView (+winnerFacts, REVIEW_CHECKLIST). */

import { Card, Lbl, SectionTitle, Pill, BtnNeutral, ScoreBar, TIER_STYLE, SEARCH_LABEL_C, StatusPill, AiTag, T, MONO, FONT, fmt, escapeHtml, OpenLink, wireOpenLinks} from "../../ui.js";
import { searchLabel, aiLabel, pathOf } from "@agf/analyzer";
import { BackBar } from "./widgets.js";
import { AioLabel } from "../../ui.js";

const REVIEW_CHECKLIST = ["Does it answer questions directly, near the top?", "Does it state specific facts, numbers, and steps?", "Does it compare options side by side?", "Is it structured with clear headings AI can lift from?", "Do related pages link to it?"];

/* facts we can actually compute from the uploaded files — no page is ever crawled */
function winnerFacts(w, R) {
  const pageTopics = R.topics.filter(t => t.page === w.page);
  const qs = pageTopics.flatMap(t => t.queries);
  const tiles = [{ label: "Share of site AI", value: (w.aiShare * 100).toFixed(1) + "%" }];
  if (w.aiIdx != null) tiles.push({ label: "vs expected (est.)", value: "~" + w.aiIdx.toFixed(1) + "\u00d7" });
  if (qs.length) tiles.push({ label: "Matched searches", value: fmt(qs.length) });
  if (w.clicks) tiles.push({ label: "Google clicks", value: fmt(w.clicks) });
  if (w.pos) tiles.push({ label: "Avg position", value: String(w.pos) });
  const pills = [];
  const seg = pathOf(w.page).split("/").filter(Boolean)[0];
  if (seg) {
    const siblings = R.winners.filter(x => x.id !== w.id && pathOf(x.page).split("/").filter(Boolean)[0] === seg).length;
    pills.push({ text: "/" + escapeHtml(seg) + (siblings ? ` · ${siblings} more winner${siblings > 1 ? "s" : ""}` : ""), c: T.violet, bg: T.violetSoft });
  }
  if (qs.length) {
    const qq = qs.filter(q => /^(what|how|which|are|is|can|should|do|does|why|when)\b/.test(q.query)).length;
    if (qq) pills.push({ text: `${Math.round(100 * qq / qs.length)}% question searches`, c: T.blue, bg: T.blueSoft });
  }
  if (R.meta.hasSerp) {
    const aioQ = qs.filter(q => q.aio);
    if (aioQ.length) pills.push({ text: `Cited in ${aioQ.filter(q => q.cited).length} of ${aioQ.length} AIO keywords`, c: T.magenta, bg: T.magentaSoft, aio: true });
  }
  return { tiles, pills, qs };
}

export function renderWinners(el, { R, selected, setSelected, openTopic }) {
  if (selected) {
    const w = R.winners.find(x => x.id === selected);
    if (!w) { setSelected(null); return; }
    const applyTo = R.opportunities.slice(0, 3);
    const wf = winnerFacts(w, R);
    el.innerHTML = `
      ${BackBar("All winners", 'id="winners-back"')}
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:4px">
        <h1 style="font-size:19px;font-weight:800;letter-spacing:-0.4px;margin:0;font-family:${MONO};color:${T.violet}">${escapeHtml(pathOf(w.page))}</h1>${OpenLink(w.page, 14)}
        ${AiTag("AI WINNER")}
      </div>
      <div style="font-size:13px;font-weight:700;color:${T.ink};margin-bottom:18px">${escapeHtml(w.name)} · <span style="font-family:${MONO};font-size:11px;color:${T.muted};font-weight:500">${escapeHtml(w.page)}</span></div>
      <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:16px;margin-bottom:16px">
        ${Card(`
          ${SectionTitle("Why it's interesting")}
          <p style="font-size:13px;color:${T.sub};line-height:1.6;margin:0 0 14px">
            ${w.aiIdx != null
              ? `This page receives an estimated <b style="font-family:${MONO};color:${T.magenta}">~${w.aiIdx.toFixed(1)}×</b> the AI visibility we'd expect from its traditional Search performance (${searchLabel(w.searchScore || 0).toLowerCase()} Google Search${w.pos ? `, avg position ${w.pos}` : ""}).`
              : `This page carries <b style="font-family:${MONO};color:${T.magenta}">${(w.aiShare * 100).toFixed(1)}%</b> of your site's AI impressions even though it doesn't appear among your top search queries — AI results appear to favor it.`}
          </p>
          ${w.searchScore != null ? ScoreBar("Google Search strength", w.searchScore, T.violet) : ""}
          ${ScoreBar("AI visibility", w.aiScore, T.magenta)}`,
        {})}
        ${Card(`
          ${SectionTitle("What the data shows", { right: "from your uploaded data" })}
          <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:8px;margin-bottom:10px">
            ${wf.tiles.map(x => `
              <div style="background:${T.bg};border:1px solid ${T.lineSoft};border-radius:10px;padding:9px 11px">
                ${Lbl(x.label, "margin-bottom:2px")}
                <div style="font-family:${MONO};font-size:15px;font-weight:800;color:${T.magenta}">${x.value}</div>
              </div>`).join("")}
          </div>
          ${wf.pills.length > 0 ? `
            <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:4px">
              ${wf.pills.map(pl => Pill(pl.c, pl.bg, pl.aio ? AioLabel(pl.text, 10) : pl.text)).join("")}
            </div>` : ""}
          <div style="margin-top:12px">
            ${Lbl("Worth checking on the page")}
            <div style="font-size:11px;color:${T.muted};margin:2px 0 8px">We don't crawl your pages, so open it and look for the patterns that commonly go with strong AI visibility:</div>
            ${REVIEW_CHECKLIST.map(x => `
              <div style="display:flex;align-items:flex-start;gap:7px;font-size:12px;color:${T.sub};font-weight:500;margin-bottom:5px">
                <span style="width:5px;height:5px;border-radius:50%;background:${T.magenta};margin-top:6px;flex-shrink:0"></span>${x}
              </div>`).join("")}
          </div>`,
        { ai: true })}
      </div>
      ${Card(`
        ${SectionTitle("Apply this elsewhere")}
        <p style="font-size:12.5px;color:${T.sub};margin:0 0 12px">Whatever this page is doing right, these weaker pages are the first places to try repeating it:</p>
        ${applyTo.map(t => `
          <div style="display:flex;align-items:center;justify-content:space-between;padding:9px 0;border-bottom:1px solid ${T.lineSoft};gap:12px">
            <div style="min-width:0">
              <div style="font-weight:700;font-size:13px">${escapeHtml(t.name)}</div>
              <div style="font-size:11px;color:${t.page ? T.violet : T.muted};font-family:${MONO}">${escapeHtml(t.page || "page not identified")}</div>
            </div>
            <div style="display:flex;align-items:center;gap:10px;flex-shrink:0">
              ${Pill(TIER_STYLE[t.tier].c, TIER_STYLE[t.tier].bg, t.tier)}
              ${BtnNeutral("View opportunity", { small: true, attrs: `data-open-topic="${t.id}"` })}
            </div>
          </div>`).join("")}`,
      {})}`;

    el.querySelector("#winners-back").addEventListener("click", () => setSelected(null));
    wireOpenLinks(el);
    el.querySelectorAll("[data-open-topic]").forEach(b => b.addEventListener("click", () => openTopic(b.dataset.openTopic)));
    return;
  }

  /* list */
  el.innerHTML = `
    <div style="margin-bottom:16px">
      <div style="font-size:15px;font-weight:800;margin-bottom:3px">What's already working</div>
      <div style="font-size:12.5px;color:${T.sub}">These pages appear to perform unusually well in AI results. They may contain patterns worth repeating.</div>
    </div>
    ${R.winners.length === 0 ? Card(`<div style="text-align:center;padding:40px"><div style="font-weight:700;margin-bottom:4px">No clear AI winners yet</div><div style="color:${T.sub};font-size:13px">None of your pages get meaningfully more AI visibility than their Search performance predicts.</div></div>`, { style: "text-align:center" }) : ""}
    <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr) minmax(0,1fr);gap:12px">
      ${R.winners.map(w => Card(`
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px">
          <div style="display:flex;align-items:center;min-width:0"><span style="font-family:${MONO};font-weight:700;font-size:12.5px;color:${T.violet};overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escapeHtml(pathOf(w.page))}</span>${OpenLink(w.page, 11)}</div>
          ${AiTag(w.aiIdx != null ? "~" + w.aiIdx.toFixed(1) + "\u00d7 expected" : "AI-only")}
        </div>
        <div style="font-size:13px;font-weight:700;color:${T.ink};margin:3px 0 12px">${escapeHtml(w.name)}</div>
        <div style="display:flex;gap:14px;margin-bottom:12px;flex-wrap:wrap">
          <div>${Lbl("Google Search")}<div style="font-size:12.5px;font-weight:800;color:${w.searchScore == null ? T.muted : SEARCH_LABEL_C[searchLabel(w.searchScore)]}">${w.searchScore == null ? "\u2014" : searchLabel(w.searchScore)}</div></div>
          <div>${Lbl("Clicks")}<div style="font-size:12.5px;font-weight:800;font-family:${MONO};color:${T.ink}">${w.clicks ? fmt(w.clicks) : "\u2014"}</div></div>
          <div>${Lbl("AI visibility", `color:${T.magenta}`)}<div style="font-size:12.5px;font-weight:800;color:${T.magenta}">${aiLabel(w.aiScore)}</div></div>
        </div>
        <div style="font-size:12px;color:${T.sub};line-height:1.5;margin-bottom:12px">${w.aiIdx != null ? "This page appears to receive significantly more AI visibility than we'd expect from its traditional Search performance." : "This page shows up in AI results even though it isn't among your top search queries."}</div>
        ${BtnNeutral("See why it works", { small: true })}`,
      { ai: true, attrs: `data-select-winner="${w.id}" class="click-card"` })).join("")}
    </div>`;

  wireOpenLinks(el);
  el.querySelectorAll("[data-select-winner]").forEach(c => c.addEventListener("click", () => setSelected(c.dataset.selectWinner)));
}
